// CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
// Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
// SPDX-License-Identifier: LicenseRef-Cercle-Interne
//
// Fichier : apps/web/src/lib/store.ts
// Rôle : porte l'état partagé de l'interface.
// Référence : CIG-SPECIFICATION.md §5.2, §22.2.

import { defineStore } from 'pinia';
import { call, ouvrirCanal, type CigEventClient } from './api.ts';
import { substituer } from './fondu.ts';

export interface Profil {
  sub: string;
  displayName: string | null;
  groups: string[];
  roles: string[];
  isAdmin: boolean;
  features: string[];
  impersonatedBy: string | null;
}

export interface NoeudVue {
  id: string;
  kind: 'file' | 'folder';
  name: string;
  parentId: string | null;
  headVersionId: string | null;
  tags: string[];
  aiSummary: string | null;
  aiState: string;
  formatWarning: string | null;
  createdAt: string;
  deletedAt: string | null;
  legalHold: boolean;
  metadata: Record<string, unknown>;
}

/** Entrée listée : le nœud, le droit effectif et son cartouche de version. */
export interface EntreeExplorateur {
  node: NoeudVue;
  right: string;
  /** Séquence de la version de tête, ou `null` pour un dossier. */
  headSeq: number | null;
  size: number;
  /**
   * Détenteur du bail actif, ou `null`. Un objet coupé par un autre
   * utilisateur s'affiche estompé, en attente de son déplacement.
   */
  lockedBy: string | null;
  /**
   * État de la vignette servie par la plateforme : `ready`, `pending`,
   * `unavailable`, `failed`, ou `null` si le rendu n'a pas été tenté.
   */
  thumb: string | null;
  /** Objet en favori pour le demandeur. Le favori est personnel. */
  favorite: boolean;
}

export const useSession = defineStore('session', {
  state: () => ({
    profil: null as Profil | null,
    chargement: true,
    erreur: null as string | null,
    /** Notifications non lues, alimentées par le canal temps réel. */
    notifications: [] as { id: string; kind: string; payload: Record<string, unknown> }[],
    /** Plateforme en lecture seule : maintenance ou mode reprise. */
    lectureSeule: false,
    /** Objets en corbeille, pour le cartouche de la barre latérale. */
    corbeille: 0,
    /** Revues d'accès ouvertes qui vous sont adressées (spécification §18.8). */
    revues: 0,
  }),
  actions: {
    async charger(): Promise<void> {
      this.chargement = true;
      try {
        this.profil = await call<Profil>('me.profile');
        const sante = await fetch('/api/v1/health', { credentials: 'same-origin' })
          .then((r) => r.json() as Promise<{ readOnly: boolean }>)
          .catch(() => ({ readOnly: false }));
        this.lectureSeule = sante.readOnly;
        await Promise.all([
          this.chargerNotifications(),
          this.chargerCorbeille(),
          this.chargerRevues(),
        ]);
      } catch (e) {
        this.erreur = e instanceof Error ? e.message : String(e);
      } finally {
        this.chargement = false;
      }
    },

    async chargerNotifications(): Promise<void> {
      const r = await call<{ notifications: { id: string; kind: string; payload: Record<string, unknown> }[] }>(
        'me.notification.list',
        { unreadOnly: true, limit: 50 },
      );
      this.notifications = r.notifications;
    },

    /**
     * Décompte de la corbeille. Seul le total est demandé : la liste complète
     * n'a pas à être rapatriée pour afficher un cartouche.
     *
     * Un échec laisse le décompte inchangé : un cartouche indisponible ne doit
     * pas faire échouer le chargement de la coque.
     */
    async chargerCorbeille(): Promise<void> {
      try {
        const r = await call<{ total: number }>('node.trashed', { limit: 1 });
        this.corbeille = r.total;
      } catch {
        // Décompte indisponible : le cartouche conserve sa dernière valeur.
      }
    },

    /**
     * Décompte des revues d'accès ouvertes adressées à l'utilisateur. Une
     * charge de gouvernance qu'il faudrait aller chercher n'est pas traitée :
     * elle se porte en cartouche, comme la corbeille.
     */
    async chargerRevues(): Promise<void> {
      try {
        const r = await call<{ reviews: { state: string }[] }>('review.list', { state: 'open' });
        this.revues = r.reviews.length;
      } catch {
        // Décompte indisponible : le cartouche conserve sa dernière valeur.
      }
    },

    async marquerLues(ids: string[]): Promise<void> {
      if (ids.length === 0) return;
      await call('me.notification.read', { ids });
      this.notifications = this.notifications.filter((n) => !ids.includes(n.id));
    },

    peut(feature: string): boolean {
      return this.profil?.features.includes(feature) ?? false;
    },
  },
});

/**
 * Mode d'affichage de l'explorateur. Un seul ecran sert toutes les vues : ce
 * qui change est la racine du fil d'Ariane et la source des entrees, jamais la
 * presentation. Un utilisateur n'a pas a reapprendre son explorateur parce
 * qu'il regarde ses documents recents plutot qu'un dossier.
 */
export type ModeExplorateur =
  | 'dossier' | 'recent' | 'shared' | 'trashed' | 'favorites' | 'recherche';

/** Racine affichee par le fil d'Ariane, selon le mode. */
export const RACINES: Record<ModeExplorateur, { libelle: string; icone: string }> = {
  dossier: { libelle: 'Racine', icone: 'accueil' },
  recent: { libelle: 'Récents', icone: 'horloge' },
  shared: { libelle: 'Partagé avec moi', icone: 'partage' },
  trashed: { libelle: 'Corbeille', icone: 'corbeille' },
  favorites: { libelle: 'Favoris', icone: 'etoile' },
  recherche: { libelle: 'Recherche', icone: 'recherche' },
};

/** Capacite interrogee par chaque vue transverse. */
const CAPACITES: Record<'recent' | 'shared' | 'trashed' | 'favorites', string> = {
  recent: 'node.recent',
  shared: 'node.shared',
  trashed: 'node.trashed',
  favorites: 'node.favorites',
};

export const useExplorateur = defineStore('explorateur', {
  state: () => ({
    mode: 'dossier' as ModeExplorateur,
    dossierCourant: null as string | null,
    chemin: [] as { id: string; name: string }[],
    entrees: [] as EntreeExplorateur[],
    selection: new Set<string>(),
    chargement: false,
    erreur: null as string | null,
    /**
     * Contenu visible. Il passe à faux le temps du fondu sortant : la liste et
     * le fil d'Ariane ne sont substitués qu'une fois hors de vue.
     */
    visible: true,
    /** Droit effectif du demandeur sur le dossier courant. */
    droit: null as string | null,
    /** Nombre de branches concurrentes par nœud. */
    concurrents: {} as Record<string, number>,
    /** Présence des autres utilisateurs sur le dossier courant. */
    presence: [] as { sub: string; displayName: string | null }[],
    fermerCanal: null as (() => void) | null,
  }),
  actions: {
    /**
     * Ouvre un dossier. La liste et le fil d'Ariane ne changent qu'une fois le
     * fondu sortant achevé : substituer en cours de fondu ferait apparaître
     * l'ancien et le nouveau contenu superposés à mi-opacité.
     */
    async ouvrir(nodeId?: string): Promise<void> {
      this.chargement = true;
      this.erreur = null;
      try {
        this.mode = 'dossier';
        await substituer(
          (masque) => (this.visible = !masque),
          async () => {
            const liste = await call<{
              parentId: string;
              items: EntreeExplorateur[];
            }>('node.list', nodeId ? { nodeId } : {});
            const detail = await call<{
              path: { id: string; name: string }[];
              effectiveRight: string | null;
            }>('node.get', { nodeId: liste.parentId });
            return { liste, detail };
          },
          ({ liste, detail }) => {
            this.dossierCourant = liste.parentId;
            this.entrees = liste.items;
            this.selection = new Set();
            this.chemin = detail.path;
            this.droit = detail.effectiveRight;
          },
        );
      } catch (e) {
        this.erreur = e instanceof Error ? e.message : String(e);
        this.entrees = [];
      } finally {
        this.chargement = false;
      }
    },

    /**
     * Ouvre une vue transverse. Les entrees viennent d'une capacite dediee, le
     * fil d'Ariane se reduit a sa racine, et il n'y a pas de dossier courant :
     * rien ne s'y depose, rien ne s'y cree.
     */
    async ouvrirVue(vue: 'recent' | 'shared' | 'trashed' | 'favorites'): Promise<void> {
      this.chargement = true;
      this.erreur = null;
      try {
        this.mode = vue;
        await substituer(
          (masque) => (this.visible = !masque),
          () => call<{ items: EntreeExplorateur[] }>(CAPACITES[vue], {}),
          (r) => {
            this.dossierCourant = null;
            this.entrees = r.items;
            this.selection = new Set();
            this.chemin = [];
            this.droit = null;
          },
        );
      } catch (e) {
        this.erreur = e instanceof Error ? e.message : String(e);
        this.entrees = [];
      } finally {
        this.chargement = false;
      }
    },

    /**
     * Affiche les résultats d'une recherche dans la grille de l'explorateur.
     * La racine du fil d'Ariane devient « Recherche » : l'utilisateur voit
     * qu'il ne regarde plus un dossier, et par quoi y revenir.
     *
     * Une requête vide ne vide pas la vue : elle n'est simplement pas émise.
     * C'est à l'appelant de rouvrir le dossier qu'il quittait.
     */
    async rechercher(requete: string): Promise<void> {
      if (requete.trim().length < 2) return;
      this.chargement = true;
      this.erreur = null;
      try {
        this.mode = 'recherche';
        await substituer(
          (masque) => (this.visible = !masque),
          () => call<{ items: EntreeExplorateur[] }>('search.query', { query: requete, limit: 100 }),
          (r) => {
            this.dossierCourant = null;
            this.entrees = r.items;
            this.selection = new Set();
            this.chemin = [];
            this.droit = null;
          },
        );
      } catch (e) {
        this.erreur = e instanceof Error ? e.message : String(e);
        this.entrees = [];
      } finally {
        this.chargement = false;
      }
    },

    /**
     * Branche le canal temps réel. Les vues s'actualisent d'elles-mêmes ;
     * l'utilisateur ne recharge jamais (spécification §1.5).
     */
    brancherTempsReel(): void {
      this.fermerCanal?.();
      this.fermerCanal = ouvrirCanal((e: CigEventClient) => {
        // Un objet créé par quelqu'un d'autre dans le dossier ouvert n'est pas
        // encore listé : le reconnaître à son parent est le seul moyen de le
        // voir apparaître sans recharger.
        const dansLeDossier =
          (e.data as { parentId?: string } | undefined)?.parentId === this.dossierCourant;
        const concerne =
          e.nodeId === this.dossierCourant ||
          dansLeDossier ||
          this.entrees.some((x) => x.node.id === e.nodeId);
        if (!concerne) return;

        if (e.type === 'node.ai_state' && e.nodeId) {
          const entree = this.entrees.find((x) => x.node.id === e.nodeId);
          if (entree) entree.node.aiState = String(e.data?.state ?? 'idle');
          return;
        }
        if (e.type === 'version.concurrent' && e.nodeId) {
          this.concurrents[e.nodeId] = (this.concurrents[e.nodeId] ?? 0) + 1;
          return;
        }
        if (e.type === 'node.thumbnail' && e.nodeId) {
          // La vignette vient d'être rendue : seule l'entrée concernée change,
          // et son image apparaît sans que la grille bouge.
          void this.rafraichirEntree(e.nodeId);
          return;
        }

        // Un objet qui disparaît est retiré, un objet qui change est relu.
        // Recharger le dossier entier ferait clignoter la grille de tous les
        // spectateurs à chaque geste d'un seul (spécification §5.1.7).
        if (e.nodeId && e.nodeId !== this.dossierCourant) {
          if (e.type === 'node.trashed' || e.type === 'node.purged') {
            this.retirerEntrees([e.nodeId]);
          } else {
            void this.rafraichirEntree(e.nodeId);
          }
          return;
        }

        // L'événement porte sur le dossier ouvert lui-même : son contenu a pu
        // changer d'une manière que rien ne décrit ici. C'est le seul cas qui
        // justifie encore une relecture complète.
        void this.ouvrir(this.dossierCourant ?? undefined);
      });
    },

    debrancherTempsReel(): void {
      this.fermerCanal?.();
      this.fermerCanal = null;
    },

    // ----------------------------------------------------------------
    // Mise à jour différentielle (spécification §5.1.7)
    // ----------------------------------------------------------------
    //
    // Recharger le dossier entier pour un objet qui change fait clignoter la
    // grille, perd la position de défilement et rejoue chaque vignette. Les
    // actions courantes touchent une entrée, parfois deux : seules celles-là
    // doivent bouger.
    //
    // Le rechargement complet demeure, mais pour ce qu'il sait seul faire :
    // changer de dossier, ou reprendre après une erreur.

    /**
     * Retire des entrées de la vue courante. Sert à la mise en corbeille, au
     * déplacement vers un autre dossier, et à la purge.
     */
    retirerEntrees(ids: readonly string[]): void {
      const aRetirer = new Set(ids);
      if (aRetirer.size === 0) return;
      this.entrees = this.entrees.filter((e) => !aRetirer.has(e.node.id));
      const s = new Set(this.selection);
      for (const id of aRetirer) s.delete(id);
      this.selection = s;
    },

    /**
     * Insère ou remplace une entrée. Un objet déposé apparaît ainsi sans que
     * le reste de la grille ne bouge, et un objet renommé garde sa place le
     * temps que le tri le déplace.
     */
    poserEntree(entree: EntreeExplorateur): void {
      const i = this.entrees.findIndex((e) => e.node.id === entree.node.id);
      if (i >= 0) this.entrees[i] = entree;
      else this.entrees.push(entree);
    },

    /** Applique un changement partiel à une entrée présente. */
    majEntree(nodeId: string, patch: Partial<EntreeExplorateur>): void {
      const e = this.entrees.find((x) => x.node.id === nodeId);
      if (!e) return;
      Object.assign(e, patch);
    },

    /** Applique un changement partiel au nœud d'une entrée présente. */
    majNoeud(nodeId: string, patch: Partial<NoeudVue>): void {
      const e = this.entrees.find((x) => x.node.id === nodeId);
      if (!e) return;
      Object.assign(e.node, patch);
    },

    /**
     * Recharge une seule entrée depuis le serveur.
     *
     * Employée après une action dont le résultat n'est pas entièrement
     * déductible côté navigateur : un dépôt produit une version, une taille,
     * un état de vignette et un droit effectif que seul le serveur connaît.
     * Une entrée devenue inaccessible ou supprimée est retirée.
     */
    async rafraichirEntree(nodeId: string): Promise<void> {
      try {
        const r = await call<{ items: EntreeExplorateur[] }>('node.list', {
          parentId: this.dossierCourant,
          nodeIds: [nodeId],
        });
        const entree = r.items[0];
        if (entree) this.poserEntree(entree);
        else this.retirerEntrees([nodeId]);
      } catch {
        // Le serveur n'a pas répondu : la vue garde ce qu'elle affiche plutôt
        // que de se vider. L'écart se corrigera au prochain chargement.
      }
    },

    basculerSelection(id: string): void {
      const s = new Set(this.selection);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      this.selection = s;
    },

    toutSelectionner(): void {
      this.selection = new Set(this.entrees.map((e) => e.node.id));
    },

    viderSelection(): void {
      this.selection = new Set();
    },
  },
});
